import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Lightbulb,
  MessageCircle,
  Send,
  Target,
  Users,
  X,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../api';
import AiDisclaimerCard from '../AiDisclaimerCard';

const batchLabel = (student) => {
  const parts = [student.graduation_level, student.program, student.branch || student.department]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const year = String(student.academic_batch || student.graduation_year || '').trim();
  const base = parts.join(' - ');
  return base && year ? `${base} (${year})` : base || year || 'Unknown Batch';
};

const severityClass = (severity) => `faculty-gap-severity faculty-gap-severity-${severity || 'low'}`;

const normalize = (value) => String(value || '').trim().toLowerCase();

const renderInlineMarkdown = (text) => {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index}>{part.slice(1, -1)}</code>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const renderCoachMarkdown = (text) => {
  const lines = String(text || '').split(/\r?\n/);
  return lines.map((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={index} className="faculty-chat-spacer" />;

    const heading = trimmed.match(/^#{1,4}\s+(.+)$/);
    if (heading) {
      return <strong key={index} className="faculty-chat-heading">{renderInlineMarkdown(heading[1])}</strong>;
    }

    const bullet = trimmed.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      return <div key={index} className="faculty-chat-list-item"><span aria-hidden="true">•</span><span>{renderInlineMarkdown(bullet[1])}</span></div>;
    }

    const numbered = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
    if (numbered) {
      return <div key={index} className="faculty-chat-list-item"><span className="faculty-chat-number">{numbered[1]}.</span><span>{renderInlineMarkdown(numbered[2])}</span></div>;
    }

    return <p key={index} className="faculty-chat-paragraph">{renderInlineMarkdown(trimmed)}</p>;
  });
};

const studentMatchesBatch = (student, requestedBatch) => {
  const requested = normalize(requestedBatch);
  if (!requested || requested === 'all' || requested === 'all batches') return true;
  const values = [
    batchLabel(student),
    student.academic_batch,
    student.graduation_year,
    student.branch,
    student.department,
    student.program,
    student.degree,
  ].map(normalize).filter(Boolean);
  return values.includes(requested);
};

const FacultySkillGapAnalysis = ({ students = [], allowedBatches = [] }) => {
  const [batch, setBatch] = useState('All Assigned Batches');
  const [role, setRole] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recommendations, setRecommendations] = useState(false);
  const [chatSkill, setChatSkill] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [chat, setChat] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');

  // AI Disclaimer gate
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingAnalyzeEvent, setPendingAnalyzeEvent] = useState(null);

  const hasAllAssignedBatches = allowedBatches.some((value) => ['all', 'all batches'].includes(normalize(value)));
  const batches = useMemo(() => (
    [...new Set(allowedBatches
      .filter((value) => value && !['all', 'all batches'].includes(normalize(value)))
      .map((value) => String(value).trim()))].sort()
  ), [allowedBatches]);
  const scopedStudents = useMemo(() => {
    if (hasAllAssignedBatches) return students;
    return students.filter((student) => allowedBatches.some((value) => studentMatchesBatch(student, value)));
  }, [allowedBatches, hasAllAssignedBatches, students]);

  useEffect(() => {
    if (batch !== 'All Assigned Batches' && !batches.includes(batch)) {
      setBatch('All Assigned Batches');
    }
  }, [batch, batches]);

  const analyze = async (event) => {
    event.preventDefault();

    // Show disclaimer once per session before first analysis
    if (!sessionStorage.getItem('ai_disclaimer_agreed')) {
      setPendingAnalyzeEvent(event);
      setShowDisclaimer(true);
      return;
    }

    const cleanRole = role.trim();
    if (cleanRole.length < 2) {
      setError('Enter a target role, for example “Full Stack Developer”.');
      return;
    }
    setLoading(true);
    setError('');
    setAnalysis(null);
    setRecommendations(false);
    try {
      const response = await api.post('/faculty/insights/skill-gap/analyze', {
        role: cleanRole,
        batch: batch === 'All Assigned Batches' ? null : batch,
      });
      setAnalysis(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Skill-gap analysis could not be completed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const askCoach = async (event) => {
    event.preventDefault();
    const message = chatMessage.trim();
    if (!message || !chatSkill || !analysis) return;
    setChatLoading(true);
    setChatError('');
    setChat((previous) => [...previous, { from: 'faculty', text: message }]);
    setChatMessage('');
    try {
      const response = await api.post('/faculty/insights/skill-gap/chat', {
        role: analysis.role,
        batch: analysis.batch === 'All Assigned Batches' ? null : analysis.batch,
        skill: chatSkill.skill,
        message,
      });
      setChat((previous) => [...previous, { from: 'coach', text: response.data?.answer || 'No answer was returned.' }]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setChatError(typeof detail === 'string' ? detail : 'Skill Coach is temporarily unavailable.');
    } finally {
      setChatLoading(false);
    }
  };

  const closeChat = () => {
    setChatSkill(null);
    setChat([]);
    setChatMessage('');
    setChatError('');
  };

  const chartData = (analysis?.skills || []).map((item) => ({
    skill: item.skill.length > 18 ? `${item.skill.slice(0, 18)}…` : item.skill,
    required: item.threshold_percent,
    having: item.coverage_percent,
  }));

  const actionableSkills = (analysis?.skills || []).filter((item) => item.lacking > 0 || item.not_assessed > 0);

  return (
    <section className="faculty-skill-gap-panel" aria-labelledby="faculty-skill-gap-title">
      <div className="faculty-skill-gap-header">
        <div>
          <h3 id="faculty-skill-gap-title"><Target size={21} /> Batch Skill Gap Analysis</h3>
          <p>Compare any batch with the skills required for a target role. Unassessed students are kept separate from skill gaps.</p>
        </div>
        {analysis && (
          <div className="faculty-readiness-badge">
            <span>Batch readiness</span>
            <strong>{analysis.readiness_percent}%</strong>
          </div>
        )}
      </div>

      <form className="faculty-skill-gap-controls" onSubmit={analyze}>
        <label>
          <span>Select batch</span>
          <select value={batch} onChange={(event) => setBatch(event.target.value)} disabled={!allowedBatches.length}>
            <option>All Assigned Batches</option>
            {batches.map((value) => <option key={value}>{value}</option>)}
          </select>
        </label>
        <label className="faculty-role-input">
          <span>Target job role</span>
          <input
            value={role}
            onChange={(event) => setRole(event.target.value)}
            placeholder="e.g. Data Analyst or React Developer"
            maxLength={120}
          />
        </label>
        <button type="submit" className="faculty-skill-gap-primary" disabled={loading || !scopedStudents.length}>
          <Target size={17} />
          {loading ? 'Analyzing...' : 'Analyze Skill Gap'}
        </button>
      </form>

      {error && <div className="faculty-skill-gap-error" role="alert"><AlertTriangle size={17} />{error}</div>}
      {!allowedBatches.length && !loading && (
        <div className="faculty-skill-gap-empty"><Users size={30} /><strong>No batches are assigned</strong><span>Skill-gap analysis becomes available after a batch is assigned to you.</span></div>
      )}
      {allowedBatches.length > 0 && !scopedStudents.length && !loading && (
        <div className="faculty-skill-gap-empty"><Users size={30} /><strong>No students are available</strong><span>Students assigned to your university will appear here once their profiles are available.</span></div>
      )}
      {!analysis && !error && allowedBatches.length > 0 && scopedStudents.length > 0 && !loading && (
        <div className="faculty-skill-gap-empty"><Target size={30} /><strong>Choose a batch and target role</strong><span>Run an analysis to see required skills, student coverage, and upskilling actions.</span></div>
      )}

      {analysis && (
        <>
          <div className="faculty-skill-gap-summary">
            <div><Users size={19} /><strong>{analysis.student_count}</strong><span>students in scope</span></div>
            <div><CheckCircle2 size={19} /><strong>{analysis.assessed_student_count}</strong><span>students with skill data</span></div>
            <div><AlertTriangle size={19} /><strong>{actionableSkills.filter((item) => item.severity === 'critical').length}</strong><span>critical skill gaps</span></div>
          </div>

          {analysis.data_status === 'insufficient' && (
            <div className="faculty-skill-gap-warning"><AlertTriangle size={17} /><span>There is not enough assessed skill data for this batch. Assign skill assessments before treating missing data as a gap.</span></div>
          )}

          <div className="faculty-skill-gap-chart-grid">
            <div className="faculty-skill-gap-card faculty-skill-gap-chart">
              <h4>Required level vs student coverage</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 10, right: 12, left: 12, bottom: 55 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="skill" angle={-28} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend />
                  <Bar dataKey="required" name="Required level" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="having" name="Students with skill" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="faculty-skill-gap-card faculty-skill-gap-actions">
              <div className="faculty-skill-gap-card-title"><h4>Recommended upskilling plan</h4><Lightbulb size={19} /></div>
              <p>Prioritize the skills with the largest number of students below the required level.</p>
              <button type="button" className="faculty-recommend-btn" onClick={() => setRecommendations((value) => !value)} disabled={!actionableSkills.length}>
                <Lightbulb size={17} />{recommendations ? 'Hide Recommendations' : 'Recommend Upskilling Plan'}
              </button>
              {recommendations && (
                <div className="faculty-recommendation-list">
                  {actionableSkills.sort((a, b) => (b.lacking + b.not_assessed) - (a.lacking + a.not_assessed)).slice(0, 4).map((item) => (
                    <div key={item.skill} className="faculty-recommendation">
                      <strong>{item.skill}</strong>
                      <span>{item.lacking ? `Run a practical ${item.skill} workshop and follow-up assessment for ${item.lacking} students.` : `Assign a ${item.skill} assessment to the ${item.not_assessed} unassessed students.`}</span>
                      <button type="button" onClick={() => { setChatSkill(item); setChat([]); }}><MessageCircle size={15} />Ask Skill Coach</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="faculty-skill-gap-card faculty-skill-gap-table-card">
            <div className="faculty-skill-gap-card-title"><h4>Required skill comparison</h4><span>{analysis.role} · {analysis.batch}</span></div>
            <div className="faculty-skill-gap-table-wrap">
              <table>
                <thead><tr><th>Required skill</th><th>Declared</th><th>Having</th><th>Lacking</th><th>Not assessed</th><th>Coverage</th><th>Gap</th><th /></tr></thead>
                <tbody>
                  {analysis.skills.map((item) => (
                    <tr key={item.skill}>
                      <td><strong>{item.skill}</strong><small>{item.required_level} · {item.importance} priority</small></td>
                      <td>{item.declared}</td><td>{item.having}</td><td>{item.lacking}</td><td>{item.not_assessed}</td><td>{item.coverage_percent}%</td>
                      <td><span className={severityClass(item.severity)}>{item.severity === 'not_assessed' ? 'Not assessed' : item.severity}</span></td>
                      <td><button type="button" className="faculty-coach-icon" title={`Ask Skill Coach about ${item.skill}`} onClick={() => { setChatSkill(item); setChat([]); }}><MessageCircle size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {chatSkill && (
        <div className="faculty-skill-coach" role="dialog" aria-modal="true" aria-labelledby="faculty-skill-coach-title">
          <div className="faculty-skill-coach-header"><div><Bot size={19} /><strong id="faculty-skill-coach-title">{chatSkill.skill} Skill Coach</strong></div><button type="button" onClick={closeChat} aria-label="Close Skill Coach"><X size={19} /></button></div>
          <div className="faculty-skill-coach-context">{analysis.role} · {analysis.batch} · {chatSkill.lacking} lacking · {chatSkill.not_assessed} not assessed</div>
          <div className="faculty-skill-coach-messages">
            {!chat.length && <p className="faculty-skill-coach-placeholder">Ask how to teach, practice, assess, or group students for this skill.</p>}
            {chat.map((item, index) => <div key={`${item.from}-${index}`} className={`faculty-chat-message faculty-chat-${item.from}`}>{item.from === 'coach' ? renderCoachMarkdown(item.text) : item.text}</div>)}
            {chatLoading && <div className="faculty-chat-message faculty-chat-coach">Thinking...</div>}
          </div>
          {chatError && <div className="faculty-skill-coach-error">{chatError}</div>}
          <form onSubmit={askCoach} className="faculty-skill-coach-form"><input value={chatMessage} onChange={(event) => setChatMessage(event.target.value)} maxLength={1000} placeholder="Ask about this skill..." disabled={chatLoading} /><button type="submit" disabled={chatLoading || !chatMessage.trim()} aria-label="Send message"><Send size={17} /></button></form>
        </div>
      )}

      {/* AI Disclaimer Modal */}
      {showDisclaimer && (
        <AiDisclaimerCard
          featureName="AI Skill Gap Analysis"
          onAgree={() => {
            sessionStorage.setItem('ai_disclaimer_agreed', '1');
            setShowDisclaimer(false);
            if (pendingAnalyzeEvent) {
              analyze(pendingAnalyzeEvent);
              setPendingAnalyzeEvent(null);
            }
          }}
          onCancel={() => {
            setShowDisclaimer(false);
            setPendingAnalyzeEvent(null);
          }}
        />
      )}
    </section>
  );
};

export default FacultySkillGapAnalysis;
